import { AxiosInstance } from "../../axios/axiosInstance";
import { apiRoutes } from "../../utils/ApiRoutes";

// ─── Types ───────────────────────────────────────────────────────────────────

export interface IReviewWidgetConfigResponse {
  widget?: any;
  business_name: string;
  business_categories: { id: string; name: string }[];
  logo_placement: string;
  business_logo: string;
  website: string;
  phone: string;
  average_rating: number;
  total_review_count: number;
  total_reviews_count?: number;
  google_review_url: string | null;
  ai_summary: string | null;
}

export interface IGetReviewWidgetConfigPayload {
  business_id: string;
  widget_id?: string;
  show_ai_summary?: boolean;
}

export interface IGetReviewWidgetListPayload {
  business_id: string;
  widget_id?: string;
  limit?: number;
  cursor?: string;
  minimum_rating?: "all" | "3_and_up" | "4_and_up" | "5_only" | string;
  sort_by?: "recent" | "highest_rated" | "random" | string;
  hide_reviews_without_text?: boolean;
  exclude_keywords?: string[];
  hand_picked_enabled?: boolean;
  hand_picked_type?: "all" | "selected" | string;
  selected_reviews?: string[];
  rating_threshold?: number;
  text_only?: boolean;
}

// ─── API Functions ────────────────────────────────────────────────────────────

/**
 * Create a brand-new review widget.
 * Required: checkin_id + widget_name. Everything else optional.
 */
export const addReviewWidgetApi = async (payload: any) => {
  const response = await AxiosInstance.post(apiRoutes.addReviewWidget, payload);
  return response;
};

/**
 * Update review-kind-specific settings on an existing widget.
 * Required: widget_id. Partial deep-merge — send only changed fields.
 * For common fields (name, border_config, theme_config, cta_config, branding_config)
 * use editWidgetApi (shared endpoint) instead.
 */
export const editReviewWidgetApi = async (payload: any) => {
  const response = await AxiosInstance.post(apiRoutes.editReviewWidget, payload);
  return response;
};

/**
 * Bootstrap config for the dashboard editor / preview.
 * When widget_id is passed: returns the full saved widget doc (with guaranteed 9 text_style keys),
 * business branding, google_review_url, and ai_summary.
 * When widget_id is omitted (Builder preview/Create mode): returns business-level metadata
 * (business_name, business_categories, logo_placement, business_logo, website, phone,
 * average_rating, total_review_count, google_review_url, and ai_summary if show_ai_summary is true).
 */
export const getReviewWidgetConfigApi = async (payload: IGetReviewWidgetConfigPayload) => {
  const response = await AxiosInstance.post(apiRoutes.getReviewWidgetConfig, payload);
  return response;
};

/**
 * Fetch a paginated list of curated reviews.
 * When widget_id is passed: uses the saved widget's curation_config.
 * When widget_id is omitted (Builder preview/Create mode): applies flat curation fields directly
 * (minimum_rating, sort_by, hide_reviews_without_text, exclude_keywords, hand_picked_enabled,
 * hand_picked_type, selected_reviews) against the business's real reviews.
 * Supports cursor-based pagination. Default limit: 12.
 */
export const getReviewWidgetListApi = async (payload: IGetReviewWidgetListPayload) => {
  const response = await AxiosInstance.post(apiRoutes.getReviewWidgetList, payload);
  return response;
};

/**
 * Fetch detail for a single review.
 */
export const getReviewWidgetDetailApi = async (payload: {
  business_id: string;
  widget_id: string;
  review_id: string;
}) => {
  const response = await AxiosInstance.post(apiRoutes.getReviewWidgetDetail, payload);
  return response;
};

/**
 * Fetch JSON-LD structured data for SEO.
 * Capped to 50 most recent rated reviews passing curation.
 * Wrap "business" object in { "@context": "https://schema.org", ... } before injecting.
 */
export const getReviewWidgetJsonldApi = async (payload: {
  business_id: string;
  widget_id: string;
}) => {
  const response = await AxiosInstance.post(apiRoutes.getReviewWidgetJsonld, payload);
  return response;
};

/**
 * Trigger AI Review Summary regeneration for a review widget.
 */
export const regenerateReviewSummaryApi = async (payload: {
  business_id: string;
  widget_id?: string;
}) => {
  const response = await AxiosInstance.post(
    apiRoutes.regenerateReviewWidgetSummary,
    payload,
  );
  return response;
};
